import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { User } from "../types";
import { loginUser, getUserProfile } from "../api";
import { LS_ACCESS, LS_REFRESH } from "../config/api";
import {
  AUTH_SESSION_EVENT,
  ApiError,
  clearAuthSession,
  getStoredUser,
  saveAuthSession,
  updateStoredUser,
} from "../api/utils";

// Questo file centralizza tutta la gestione dell'utente autenticato nel frontend.
// L'idea è: invece di chiedere in tanti componenti "chi è l'utente?"
// e leggere ogni volta il localStorage, creiamo un "contenitore globale"
// che conosce lo stato della sessione e lo mette a disposizione di tutta l'app.
//
// In React questo pattern si chiama Context:
// - AuthProvider = componente che contiene e distribuisce lo stato auth
// - useAuth() = scorciatoia per leggere quello stato da qualsiasi componente figlio

interface AuthContextValue {
  // Utente loggato attuale. Se è null significa "nessun utente autenticato".
  user: User | null;
  // Flag comodo per non dover scrivere ogni volta !!user nei componenti.
  isAuthenticated: boolean;
  // Flag usato durante il bootstrap iniziale:
  // finché non abbiamo verificato la sessione col backend, l'app è "in caricamento auth".
  isLoading: boolean;
  // Esegue il login lato frontend e aggiorna lo stato globale.
  login: (email: string, password: string) => Promise<User>;
  // Esegue il logout lato frontend e pulisce la sessione.
  logout: () => void;
  // Rilegge il profilo dal backend. Utile quando vogliamo sincronizzarci con la sorgente autorevole.
  refreshUser: () => Promise<User | null>;
}

// Creiamo il Context ma senza un valore iniziale reale.
// Lo inizializzeremo dentro AuthProvider.
const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Primo stato: proviamo a partire da quello che abbiamo salvato in locale.
  // Questo serve solo per avere un avvio più fluido dell'interfaccia.
  // La vera conferma della sessione arriverà comunque dal backend.
  const [user, setUser] = useState<User | null>(getStoredUser());

  // isLoading = "stiamo ancora controllando se la sessione è davvero valida?"
  const [isLoading, setIsLoading] = useState(true);

  const refreshUser = async (): Promise<User | null> => {
    // Questa funzione chiede al backend: "chi è l'utente attualmente autenticato?"
    // Se la sessione è valida, il backend risponde con il profilo utente.
    try {
      const profile = await getUserProfile();

      // Aggiorniamo sia il localStorage sia lo stato React,
      // così tutta l'app vede subito l'utente corretto.
      updateStoredUser(profile);
      setUser(profile);
      return profile;
    } catch (error) {
      // Se il backend risponde 401 significa che access token e refresh token
      // non sono più sufficienti per considerare valida la sessione.
      if (error instanceof ApiError && error.status === 401) {
        // Ripuliamo completamente la sessione locale:
        // token, utente e notifica agli altri punti dell'app.
        clearAuthSession();
        setUser(null);
        return null;
      }

      // Qualsiasi altro errore non è un "logout":
      // potrebbe essere rete, backend giù, errore inatteso...
      // quindi lo rilanciamo.
      throw error;
    }
  };

  useEffect(() => {
    // Questo effect viene eseguito una sola volta quando AuthProvider viene montato.
    // In pratica è il "controllo sessione iniziale" dell'app.
    const bootstrapSession = async () => {
      // Se non c'è nessun token salvato, non ha senso interrogare il backend:
      // possiamo già concludere che non c'è una sessione da recuperare.
      const hasSessionCandidate =
        !!localStorage.getItem(LS_ACCESS) || !!localStorage.getItem(LS_REFRESH);

      if (!hasSessionCandidate) {
        setUser(null);
        setIsLoading(false);
        return;
      }

      try {
        // Se qualche token esiste, verifichiamo davvero col backend.
        // Questo è il punto chiave che evita il falso "utente loggato"
        // basato solo sul localStorage.
        await refreshUser();
      } finally {
        // In ogni caso, dopo il tentativo iniziale smettiamo di essere in loading.
        setIsLoading(false);
      }
    };

    bootstrapSession();
  }, []);

  useEffect(() => {
    // Questo secondo effect ascolta gli "eventi di sessione" emessi dal layer API.
    // Esempio: login, logout, refresh del profilo, sessione scaduta.
    //
    // In questo modo Navbar, ProtectedRoute e tutte le pagine restano sincronizzate
    // anche se il cambiamento parte da un altro file.
    const handleSessionChanged = (event: Event) => {
      const customEvent = event as CustomEvent<{ user: User | null }>;

      // Se l'evento porta un utente lo salviamo nello stato;
      // se porta null significa che la sessione è stata rimossa.
      setUser(customEvent.detail?.user ?? null);
    };

    window.addEventListener(AUTH_SESSION_EVENT, handleSessionChanged);

    // Cleanup: quando il provider viene smontato togliamo il listener
    // per evitare memory leak o duplicazioni.
    return () => window.removeEventListener(AUTH_SESSION_EVENT, handleSessionChanged);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,

      // Booleano derivato dall'utente corrente.
      isAuthenticated: !!user,
      isLoading,
      login: async (email: string, password: string) => {
        // 1. Chiediamo al backend i token + il profilo utente.
        const session = await loginUser(email, password);

        // 2. Salviamo tutto in locale.
        // saveAuthSession emette anche l'evento globale di sessione cambiata.
        saveAuthSession(session.access, session.refresh, session.user);

        // 3. Aggiorniamo anche lo stato locale di questo provider.
        setUser(session.user);
        return session.user;
      },
      logout: () => {
        // Logout frontend:
        // - rimuoviamo token e utente salvati
        // - notifichiamo tutta l'app
        // - portiamo lo stato React a "nessun utente"
        clearAuthSession();
        setUser(null);
      },
      refreshUser,
    }),
    // useMemo evita di ricreare l'oggetto value inutilmente a ogni render.
    // Questo riduce rerender non necessari nei componenti che usano il context.
    [user, isLoading]
  );

  // Tutti i componenti figli di AuthProvider possono leggere value con useAuth().
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextValue => {
  // useContext legge il valore distribuito dal provider.
  const context = useContext(AuthContext);

  // Questa guardia serve a intercettare subito un uso scorretto:
  // se qualcuno prova a usare useAuth fuori da AuthProvider, lanciamo un errore chiaro.
  if (!context) {
    throw new Error("useAuth deve essere usato dentro AuthProvider");
  }
  return context;
};
